import { TrendingUp, Shield, Target, Sparkles, RotateCcw, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { generateRiskProfilePDF } from '../utils/generatePDF';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { getFundLogo } from './fund-logos';
import { PDFLoadingOverlay } from './PDFLoadingOverlay';
import { ClientInfoModal } from './ClientInfoModal';
import { useState } from 'react';
import { getRequiredFundLogos } from '../hooks/useImageCache';

interface Range {
  category: string;
  min: number;
  max: number;
  color: string;
}

interface Fund {
  name: string;
  composition: string;
}

interface RiskProfile {
  profile: string;
  description: string;
  scoreRange: string;
  icon: React.ReactNode;
  color: string;
  ranges: Range[];
  funds: Record<string, Fund[]>;
}

interface RiskProfileResultProps {
  score: number;
  answers: Record<string, string>;
  onRestart: () => void;
}

function getProfileFromScore(score: number, t: any): RiskProfile {
  const icons = {
    conservative: <Shield className="h-12 w-12" />,
    moderateConservative: <Target className="h-12 w-12" />,
    moderate: <Sparkles className="h-12 w-12" />,
    moderateAggressive: <TrendingUp className="h-12 w-12" />,
    aggressive: <TrendingUp className="h-12 w-12" />,
  };

  const colors = {
    conservative: 'text-blue-400',
    moderateConservative: 'text-cyan-400',
    moderate: 'text-[#90d977]',
    moderateAggressive: 'text-orange-400',
    aggressive: 'text-red-400',
  };

  let profileKey: keyof typeof t.riskProfiles;
  let colorKey: keyof typeof colors;
  
  if (score <= 20) {
    profileKey = 'conservative';
    colorKey = 'conservative';
  } else if (score <= 40) {
    profileKey = 'moderateConservative';
    colorKey = 'moderateConservative';
  } else if (score <= 60) {
    profileKey = 'moderate';
    colorKey = 'moderate';
  } else if (score <= 80) {
    profileKey = 'moderateAggressive';
    colorKey = 'moderateAggressive';
  } else {
    profileKey = 'aggressive';
    colorKey = 'aggressive';
  }

  const profileData = t.riskProfiles[profileKey];

  return {
    profile: profileData.profile,
    description: profileData.description,
    scoreRange: profileData.scoreRange,
    icon: icons[profileKey as keyof typeof icons],
    color: colors[colorKey],
    ranges: profileData.ranges,
    funds: profileData.funds,
  };
}

export function RiskProfileResult({ score, answers, onRestart }: RiskProfileResultProps) {
  const { translations: t, language } = useLanguage();
  const profileData = getProfileFromScore(score, t);

  // Filter ranges that have max > 0
  const activeRanges = profileData.ranges.filter(range => range.max > 0);

  // Calculate normalized recommended values that sum to 100%
  const calculateNormalizedValues = () => {
    // Calculate midpoints
    const midpoints = activeRanges.map(range => (range.min + range.max) / 2);
    const total = midpoints.reduce((sum, val) => sum + val, 0);
    
    // Normalize to sum to 100
    const normalized = midpoints.map(val => (val / total) * 100);
    
    // Round values
    let rounded = normalized.map(val => Math.round(val));
    
    // BUSINESS RULE: Enforce 10% minimum for any non-zero allocation
    // If a category has >0% but <10%, set it to 10%
    const belowMinimum = rounded.filter(val => val > 0 && val < 10);
    
    if (belowMinimum.length > 0) {
      // Calculate total deficit (amount needed to bring all below 10% up to 10%)
      const deficit = belowMinimum.reduce((sum, val) => sum + (10 - val), 0);
      
      // Apply 10% minimum
      rounded = rounded.map(val => val > 0 && val < 10 ? 10 : val);
      
      // Redistribute deficit from categories >= 10% (proportionally from largest)
      const aboveMinimum = rounded
        .map((val, idx) => ({ val, idx }))
        .filter(item => item.val >= 10)
        .sort((a, b) => b.val - a.val); // Sort descending
      
      let remaining = deficit;
      for (let i = 0; i < aboveMinimum.length && remaining > 0; i++) {
        const item = aboveMinimum[i];
        const reduction = Math.min(item.val - 10, Math.ceil(remaining / (aboveMinimum.length - i)));
        rounded[item.idx] -= reduction;
        remaining -= reduction;
      }
    }
    
    // Adjust to ensure sum is exactly 100, respecting 10% minimum
    const sum = rounded.reduce((a, b) => a + b, 0);
    if (sum !== 100 && rounded.length > 0) {
      const difference = 100 - sum;
      
      // Find the largest category that can absorb the adjustment without going below 10%
      const adjustable = rounded
        .map((val, idx) => ({ val, idx }))
        .filter(item => item.val > 10 || (item.val === 10 && difference > 0)) // Can reduce if >10, or increase if =10
        .sort((a, b) => b.val - a.val); // Sort descending (largest first)
      
      if (adjustable.length > 0) {
        // Apply adjustment to the largest adjustable category
        rounded[adjustable[0].idx] += difference;
      }
    }
    
    return rounded;
  };

  const normalizedValues = calculateNormalizedValues();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDownloadPDFClick = () => {
    // Open modal instead of generating PDF directly
    setIsModalOpen(true);
  };

  const handleModalConfirm = async (clientName: string, advisorName: string) => {
    setIsModalOpen(false); // Close modal
    setIsDownloading(true); // Show loading overlay
    try {
      // OPTIMIZATION: Get all fund names that will be in the PDF
      const allFundNames: string[] = [];
      for (const range of activeRanges) {
        const categoryFunds = profileData.funds[range.category];
        if (categoryFunds && categoryFunds.length > 0) {
          allFundNames.push(...categoryFunds.map(fund => fund.name));
        }
      }

      // OPTIMIZATION: Pre-load only the required logos
      const preloadedLogos = await getRequiredFundLogos(allFundNames);

      // Generate PDF
      const pdfBlob = await generateRiskProfilePDF({
        profile: profileData.profile,
        description: profileData.description,
        ranges: profileData.ranges,
        funds: profileData.funds,
        score,
        language,
        preloadedLogos, // Pass preloaded logos to PDF generator
        clientName, // Pass client name from modal
        advisorName, // Pass advisor name from modal
      });

      // Prepare allocation object
      const allocation: Record<string, number> = {};
      activeRanges.forEach((range, index) => {
        const categoryKey = range.category.toLowerCase().replace(/\s+/g, '');
        allocation[categoryKey] = normalizedValues[index];
      });

      // Save risk profile to backend
      const { saveRiskProfile, uploadPdf } = await import('../services/api');
      
      const profileResponse = await saveRiskProfile({
        name: clientName,
        email: advisorName, // Using advisorName as email for now
        language,
        answers, // Pass actual answers from wizard
        totalScore: score,
        allocation,
        riskLevel: profileData.profile,
      });

      console.log('✅ Risk profile saved:', profileResponse.id);

      // Convert PDF blob to base64
      const reader = new FileReader();
      const pdfBase64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Data = base64.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      const pdfBase64 = await pdfBase64Promise;

      // Upload PDF to backend
      const fileName = `dcs-risk-profile-${clientName.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
      await uploadPdf(profileResponse.id, pdfBase64, fileName);

      console.log('✅ PDF uploaded successfully');

      // Download PDF locally
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false); // Hide loading overlay
    }
  };

  return (
    <>
      {/* PDF Loading Overlay */}
      <PDFLoadingOverlay isVisible={isDownloading} />
      
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-4 flex flex-col items-center">
          <div className="flex items-center gap-6">
            <div className={`${profileData.color} bg-[#18181b] p-6 rounded-full border-2 border-[#3f3f46] flex-shrink-0`}>
              {profileData.icon}
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl text-white">
                {t.results.title}
              </h2>
              <div className={`text-2xl md:text-3xl ${profileData.color}`}>
                {profileData.profile}
              </div>
            </div>
          </div>
          <p className="text-white max-w-2xl text-center">
            {profileData.description}
          </p>
        </div>

        {/* Download PDF Button - Destacado arriba */}
        <div className="flex justify-center">
          <Button
            onClick={handleDownloadPDFClick}
            className="bg-[#90d977] hover:bg-[#a3e887] text-black cursor-pointer px-6 py-3 shadow-lg hover:shadow-xl transition-all"
          >
            <Download className="mr-2 h-4 w-4" />
            {t.results.downloadPDF}
          </Button>
        </div>

        {/* Bar Chart - Distribución Recomendada de Activos */}
        <Card className="bg-[#0d0d12] border-[#27272a] p-6">
          <h3 className="text-xl text-white text-center mb-6">
            {t.results.chartTitle}
          </h3>
          <div className="space-y-6">
            {activeRanges.map((range, index) => {
              // Use normalized value instead of midpoint
              const recommendedValue = normalizedValues[index];
              
              return (
                <div key={index} className="space-y-3">
                  {/* Header with category and recommended value */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0 shadow-lg" 
                        style={{ 
                          backgroundColor: range.color,
                          boxShadow: `0 0 10px ${range.color}40`
                        }}
                      />
                      <span className="text-white font-medium">{range.category}</span>
                    </div>
                    {/* Big recommended value */}
                    <div className="flex items-baseline gap-1">
                      <span 
                        className="text-3xl font-bold transition-all duration-700"
                        style={{ color: range.color }}
                      >
                        {recommendedValue}
                      </span>
                      <span className="text-xl text-[#a1a1aa]">%</span>
                    </div>
                  </div>

                  {/* Bar visualization */}
                  <div className="space-y-1.5">
                    {/* Full scale background */}
                    <div className="relative w-full h-10 bg-[#18181b] rounded-full overflow-hidden border border-[#27272a]">
                      {/* Reference range (light background) */}
                      <div 
                        className="absolute h-full transition-all duration-700 opacity-20"
                        style={{ 
                          backgroundColor: range.color,
                          left: `${range.min}%`,
                          width: `${range.max - range.min}%`
                        }}
                      />
                      
                      {/* Recommended value bar (bright) */}
                      <div 
                        className="absolute h-full transition-all duration-700 rounded-full"
                        style={{ 
                          backgroundColor: range.color,
                          width: `${recommendedValue}%`,
                          boxShadow: `0 0 15px ${range.color}60`
                        }}
                      />

                      {/* Indicator at recommended value */}
                      <div 
                        className="absolute top-0 bottom-0 w-1 bg-white transition-all duration-700 shadow-lg"
                        style={{ 
                          left: `${recommendedValue}%`,
                          boxShadow: '0 0 8px rgba(255,255,255,0.5)'
                        }}
                      />

                      {/* Grid lines for reference */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        {[25, 50, 75].map((mark) => (
                          <div 
                            key={mark}
                            className="absolute h-full w-px bg-[#27272a]"
                            style={{ left: `${mark}%` }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Range reference text */}
                    <div className="flex items-center justify-between text-xs text-[#a1a1aa] px-1">
                      <span>0%</span>
                      <span className="text-[#71717a]">
                        {t.results.rangeLabel || 'Range'}: {range.min}% - {range.max}%
                      </span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total allocation info */}
          <div className="mt-6 pt-6 border-t border-[#27272a]">
            <div className="flex items-center justify-between">
              <span className="text-[#a1a1aa] text-sm">
                {t.results.totalAllocation || 'Total Portfolio Allocation'}
              </span>
              <span className="text-2xl font-bold text-[#90d977]">
                {normalizedValues.reduce((sum, val) => sum + val, 0)}%
              </span>
            </div>
          </div>
        </Card>

        {/* Suggested Funds */}
        <Card className="bg-[#0d0d12] border-[#27272a] p-6">
          <h3 className="text-xl text-white mb-4">
            {t.results.fundsTitle}
          </h3>
          <div className="space-y-6">
            {activeRanges.map((range, index) => {
              const categoryFunds = profileData.funds[range.category];
              if (!categoryFunds || categoryFunds.length === 0) return null;

              return (
                <div key={index} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: range.color }}
                    />
                    <h4 className="text-white">{range.category}</h4>
                  </div>
                  <div className="ml-6 space-y-2">
                    {categoryFunds.map((fund, fundIndex) => {
                      const LogoComponent = getFundLogo(fund.name);
                      
                      return (
                        <div 
                          key={fundIndex} 
                          className="flex items-center gap-3 py-2 border-b border-[#27272a]/50 last:border-0"
                        >
                          {/* Icon with green circular background like fund monitor */}
                          <div className="w-12 h-12 rounded-full bg-[#90d977] flex-shrink-0 flex items-center justify-center p-2">
                            {LogoComponent ? (
                              <LogoComponent className="w-full h-full text-[#1a1b25]" />
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-[#3f3f46]" />
                            )}
                          </div>
                          {/* Fund name */}
                          <span className="text-white text-sm sm:text-base">{fund.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Note */}
        <Card className="bg-[#1a1b25] border-[#27272a] p-4">
          <p className="text-[#a1a1aa] text-sm">
            <strong className="text-white">{t.results.noteTitle}</strong> {t.results.noteText}
          </p>
        </Card>

        {/* Take Test Again - Centrado abajo */}
        <div className="flex justify-center">
          <Button
            onClick={onRestart}
            variant="outline"
            size="lg"
            className="border-[#27272a] bg-[#27272a] text-white hover:bg-[#3f3f46] hover:border-[#3f3f46] hover:text-white cursor-pointer"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t.results.restartTest}
          </Button>
        </div>
      </div>

      {/* Client Info Modal */}
      <ClientInfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </>
  );
}